import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFetch } from "@/hooks/useFetch";
import api, { getBaseId } from "@/lib/api";
import panelApi from "@/lib/panelApi";
import { usePayrollSettings } from "@/context/PayrollSettingsContext";
import { usePayBases, filterPayrollByPayBase } from "@/lib/usePayBases";
import { useBaseSalaries, salaryBaseOf } from "@/lib/useBaseSalaries";
import { useBaseComponents, baseCompMaps, applyBaseComponents, pjRatesOf } from "@/lib/baseComponents";
import { withResolvedSalary } from "@/lib/payrollCompute";
import { isBaseSalaryItem, hasBaseFlag } from "@/lib/baseSalary";

// Pipeline de cálculo da folha usada no Custo Operacional e nas Despesas:
// base de pagamento > config própria da base > salário (override > por base > global) > marcadores do painel.
export function usePayrollResolver(month, forBaseId) {
  const base = forBaseId || getBaseId() || null;
  const payCfg = useFetch("/admin/payroll/config");
  const pjSettings = useFetch("/admin/payroll-settings");
  const collabs = useQuery({ queryKey: ["all-collabs-nobase"], queryFn: async () => (await api.get("/admin/collaborators", { skipBaseId: true })).data?.collaborators || [] });
  const indivQ = useQuery({
    queryKey: ["indiv-benefits-month", month],
    queryFn: async () => {
      const res = await Promise.all([1, 2].map((q) => panelApi.get("/panel/individual-benefits", { params: { month, quinzena: q } })));
      return res.flatMap((r, i) => (r.data?.benefits || []).map((b) => ({ ...b, _q: i + 1 })));
    },
  });
  const { settings } = usePayrollSettings();
  const { data: payBases } = usePayBases();
  const { data: baseSalaries } = useBaseSalaries();
  const { data: baseComps } = useBaseComponents();

  return useMemo(() => {
    const maps = baseCompMaps(baseComps || {});
    const configById = {}, etypeOfG = {};
    Object.entries(payCfg.data?.configs || {}).forEach(([k, c]) => (c.items || []).forEach((it) => { if (it.id) { configById[it.id] = it; etypeOfG[it.id] = k; } }));
    ["ciclista_pj", "motociclista_pj"].forEach((s) => (pjSettings.data?.sections?.[s]?.items || []).forEach((it) => { if (it.id) { configById[it.id] = it; etypeOfG[it.id] = s; } }));
    const allConfig = { ...configById, ...maps.configById };
    const etypeOf = { ...etypeOfG, ...maps.etypeOf };
    const collabById = Object.fromEntries((collabs.data || []).map((c) => [c.id, c]));
    const baseOf = (uid) => salaryBaseOf(uid, base, payBases, collabById);
    const indivByUser = {};
    (indivQ.data || []).forEach((b) => { (indivByUser[b.user_id] = indivByUser[b.user_id] || []).push(b); });
    const prepare = (d) => applyBaseComponents(filterPayrollByPayBase(d, payBases, base), { baseComps, etypeOf: etypeOfG, pj: pjRatesOf(pjSettings.data), baseOf });
    const baseFlags = { ...(settings.base_salary_items || {}), ...maps.baseSalary };
    const resolveSalary = (l, n = 1) => {
      const anyFlag = hasBaseFlag(l.items, baseFlags);
      const sal = (l.items || []).find((it) => isBaseSalaryItem(it, { flags: baseFlags, anyFlag, label: allConfig[it.id]?.label, isSalary: (allConfig[it.id]?.kind ?? it.kind) === "salary" }));
      const ov = collabById[l.user_id]?.salary_override;
      const b = baseOf(l.user_id);
      const bv = sal && b ? baseSalaries?.[b]?.[etypeOf[sal.id]] : null;
      return withResolvedSalary(l, { configById: allConfig, override: ov != null ? Number(ov) : null, baseValue: bv != null ? Number(bv) : null, n, baseFlags });
    };
    const flags = {
      disabled: { ...(settings.disabled_items || {}), ...maps.disabled },
      auto: { ...(settings.auto_bonus_items || {}), ...maps.auto },
      tax: { ...(settings.tax_items || {}), ...maps.tax },
      quinzenaItems: { ...(settings.quinzena_items || {}), ...maps.quinzena },
    };
    const individualFor = (uid, q) => (indivByUser[uid] || []).filter((b) => q === "mensal" || b._q === q);
    const pjIds = new Set(["ciclista_pj", "motociclista_pj"].flatMap((s) => (pjSettings.data?.sections?.[s]?.drivers || []).map((d) => d.id)));
    const contractOf = (uid) => (pjIds.has(uid) || String(collabById[uid]?.contract_type || "").toLowerCase() === "pj" ? "pj" : "clt");
    const ready = !!payCfg.data && !!pjSettings.data && collabs.isSuccess && indivQ.isSuccess;
    // Administrativo cuja base de pagamento é outra não gera custo nesta base.
    const outOfBase = (uid) => !!base && collabById[uid]?.role === "funcionario" && !!payBases?.[uid] && payBases[uid] !== base;
    return { prepare, resolveSalary, flags, individualFor, outOfBase, contractOf, baseOf, collabOf: (uid) => collabById[uid], ready };
  }, [base, payCfg.data, pjSettings.data, collabs.data, collabs.isSuccess, indivQ.data, indivQ.isSuccess, settings, payBases, baseSalaries, baseComps]);
}
